import {
  MouseEvent, useCallback, useEffect, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import Button from '@material-ui/core/Button';
import FormDatePicker from 'src/components/Control/FormControls/FormDatePicker';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { setError } from 'src/redux/actions/errorsActions';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import { COLORS } from 'src/constants/Color';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import Subtitle from 'src/components/UI/Subtitle';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { ErrorBag } from 'src/models/ErrorModel';
import { t } from 'src/lib/language';
import CountriesSelector from 'src/components/Control/CountriesSelector';
import InvoiceValidator from 'src/validations/InvoiceValidator';
import InvoiceOrderValidator from 'src/validations/InvoiceOrderValidator';
import AddressSearch from 'src/components/Control/AddressSearch';
import { AddressModel, AddressType } from 'src/models/AddressModel';
import {
  InvoiceModel,
  InvoiceOperations,
  OrderList,
  OrderModel,
  OrderTaxModel,
  ProductOrderList,
  ProductOrderModel,
} from 'src/models/InvoiceModel';
import { getInvoiceAction, putInvoiceAction } from 'src/redux/actions/invoiceActions';
import {
  INVOICE_FIELDS,
  INVOICE_ORDER_FIELDS,
  INVOICE_PAYMENT_STATUS,
  INVOICE_PAYMENT_STATUS_COLOR,
  INVOICE_STATE,
} from 'src/constants/Invoices';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { closeModal } from 'src/redux/actions/modalActions';
import { MODALS } from 'src/models/ModalModel';
import { AUTOCOMPLETE_ID } from 'src/constants/Autocomplete';
import MetadataSelector, { MetadataType } from 'src/components/Control/MetadataSelector';
import OrdersDisplayer from 'src/components/Elements/OrderDisplayer';
import { MODALS_SIZE } from 'src/models/modal';
import { isEmpty, keyBy } from 'lodash';
import { ProductModel } from 'src/models/ProductModel';
import { ContactPayload } from 'src/models/ContactModel';
import { getContactAction } from 'src/redux/actions/contactActions';
import { getContactInfoForInvoice } from 'src/lib/ContactHelper';
import ContactSelector from 'src/components/Control/ContactSelector';
import { Tooltip } from '@material-ui/core';
import ActionButton, { MENU_BUTTON_CATEGORY } from 'src/components/UI/ActionButton';
import SaveIcon from '@material-ui/icons/Save';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import ManageAccountsButton from 'src/components/Control/Buttons/ManageAccountsButton';
import ManageDepartmentsButton
  from 'src/components/Control/Buttons/ManageDepartmentsButton';
import ManageProgramsButton from 'src/components/Control/Buttons/ManageProgramsButton';
import OrganisationBankAccountSelector
  from 'src/components/Control/OrganisationBankAccountSelector';
import MakePaymentButton from 'src/components/Control/Buttons/MakePaymentButton';
import ManageProductsButton from 'src/components/Control/Buttons/ManageProductsButton';
import DownloadSelectedInvoiceButton
  from 'src/components/Control/Buttons/DownloadSelectedInvoiceButton';
import SendInvoiceByEmailButton
  from 'src/components/Control/Buttons/SendInvoiceByEmailButton';
import DuplicateInvoiceButton from 'src/components/Control/Buttons/DuplicateInvoiceButton';
import ShowPaymentsForInvoiceButton
  from 'src/components/Control/Buttons/ShowPaymentsForInvoiceButton';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import GatewaySelector from 'src/components/Control/GatewaySelector';
import AddInvoiceContactButton
  from 'src/components/Control/Buttons/AddInvoiceContactButton';
import { openDialog } from 'src/redux/actions/dialogActions';
import { DIALOGS } from 'src/models/DialogModel';
import ReplaceInvoiceDialog from 'src/components/Dialogs/ReplaceInvoiceDialog';
import { TaxItemModel } from 'src/models/TaxListModel';
import { ProcessingType } from 'src/models/GatewayModel';
import Header from 'src/components/UI/Header';
import Row from 'src/components/UI/Row';
import Title from 'src/components/UI/Title';
import PaginationUI from 'src/components/UI/PaginationUI';
import Scroller from 'src/components/UI/Scroller';
import Col2 from 'src/components/UI/Col2';
import Amount from 'src/components/UI/Amount';
import Form from 'src/components/UI/Form';
import Warning from 'src/components/UI/Warning';
import EditIcon from 'src/components/UI/EditIcon';
import CancelInvoiceButton from 'src/components/Control/Buttons/CancelInvoiceButton';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment-timezone-all';
import { fromOrgTimezone } from 'src/lib/DateHelper';
import FundraisingSelector from 'src/components/Control/FundraisingSelector';
import AmbassadorSelector from 'src/components/Control/AmbassadorSelector';
import Col4 from 'src/components/UI/Col4';
import { FundraisingLink } from 'src/models/FundraisingModel';
import { FundraisingLinkValidator } from 'src/validations/FundraisingTransactionValidator';

type Fields = INVOICE_ORDER_FIELDS | INVOICE_FIELDS;

interface TaxListModel {
  [id: number]: TaxItemModel;
}

const EditInvoiceForm = () => {
  const dispatch = useAppDispatch();

  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const taxes = useSelector((state: Store) => {
    if (!state.taxes) return {} as TaxListModel;
    const result = {} as TaxListModel;
    state.taxes.forEach((tax) => {
      if (tax.id) result[tax.id] = tax;
    });
    return result;
  });
  const countries = useSelector(
    (state: Store) => keyBy(state.data.countries ?? [], 'id'),
  );
  const timezone = useSelector(
    (state: Store) => state.data.timezones[state.organisation.timezoneId],
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [allowedFields, setAllowedFields] = useState([] as Array<string>);

  const getStatus = useSelector((state: Store) => state.formStatus[FORM.get_invoice]);
  const putStatus = useSelector((state: Store) => state.formStatus[FORM.put_invoice]);
  const processing = () => putStatus === FORM_STATUS.processing
    || getStatus === FORM_STATUS.processing;

  const selections = useSelector(
    (state: Store) => state.selected[ENTITIES.invoices] || [],
  );

  const invoice = useSelector(
    (state: Store) => {
      if (!!state.invoices[selections[currentIndex] as number]) {
        return state.invoices[selections[currentIndex] as number] as InvoiceModel;
      }
      return {} as InvoiceModel;
    },
  );

  const [contactId, setContactId] = useState(invoice.contact_id ?? 0);

  const { contact } = useSelector(
    (state: Store) => {
      if (contactId && !!state.contacts[contactId]) {
        return state.contacts[contactId] as ContactPayload;
      }
      return {} as ContactPayload;
    },
  );

  const [invoiceState, setInvoiceState] = useState(invoice as InvoiceModel);

  const [orderState, setOrderState] = useState(
    invoice.order as ProductOrderList ?? [] as ProductOrderList,
  );

  const [fundraisingState, setFundraisingState] = useState({
    ...invoice.event,
    is_linked: !!invoice?.event?.invoice_id,
  } as FundraisingLink);

  const [invoiceDetailsViewState, setInvoiceDetailsViewState] = useState(true);

  const getAllowedFields = (currentInvoice: InvoiceModel) => {
    const invoiceFields = [
      ...Object.values(INVOICE_FIELDS),
      ...Object.values(InvoiceOperations),
    ];
    let allowedFieldList = invoiceFields;

    if (!currentInvoice.state) {
      setAllowedFields(allowedFields);
      return;
    }
    switch (currentInvoice.state) {
      case INVOICE_STATE.draft:
        allowedFieldList = invoiceFields;
        break;
      case INVOICE_STATE.canceled:
      case INVOICE_STATE.abandoned:
      case INVOICE_STATE.error:
        allowedFieldList = invoiceFields.filter((field) => field === InvoiceOperations.make_final);
        break;
      default:
        allowedFieldList = currentInvoice.payment_status === INVOICE_PAYMENT_STATUS.paid
          ? invoiceFields.filter((field) => field !== InvoiceOperations.delete_invoice)
          : invoiceFields;
    }
    setAllowedFields(allowedFieldList);
  };

  useEffect(() => {
    dispatch(setError({ [FORM.edit_invoice]: {} }));
    if (!selections[currentIndex]) {
      dispatch(closeModal({ modal: MODALS.editInvoice }));
    }
  }, []);

  useEffect(() => {
    dispatch(setError({ [FORM.edit_invoice]: {} }));
    if (selections[currentIndex]) {
      dispatch(getInvoiceAction(selections[currentIndex] as number));
    }
  }, [currentIndex]);

  useEffect(() => {
    if (isEmpty(invoice)) return;
    setInvoiceState(invoice);
    setContactId(invoice.contact_id ?? 0);
    if (invoice.order) {
      setOrderState(invoice.order);
    }
    setFundraisingState({
      ...invoice.event,
      is_linked: !!invoice.event?.invoice_id,
    } as FundraisingLink);
    getAllowedFields(invoice);
  }, [invoice]);

  useEffect(() => {
    if (!contact) return;
    const contactInfoForInvoice = getContactInfoForInvoice(contact);
    setInvoiceState({
      ...invoiceState,
      ...contactInfoForInvoice,
      contact_payer_id: invoiceState.contact_payer_id ?? invoice.contact_payer_id,
      issue_tax_receipt: !!contactInfoForInvoice.issue_tax_receipt,
      contact_salutation_id: (contact.salutation_id === 0)
        ? null
        : contact.salutation_id,
    } as InvoiceModel);
  }, [contact]);

  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'CAD',
  });

  const setAddressFromGoogle = (address: AddressModel, type: AddressType) => {
    setInvoiceState({
      ...invoiceState,
      [`${type}_address`]: address.address,
      [`${type}_city`]: address.city,
      [`${type}_state`]: address.state,
      [`${type}_country_id`]: address.country_id,
      [`${type}_zip_code`]: (!!address.zip_code) ? address.zip_code.replaceAll(' ', '') : '',
    });
  };

  const setShippingFromBilling = () => ({
    ...invoiceState,
    shipping_address: invoiceState.billing_address,
    shipping_suite: invoiceState.billing_suite,
    shipping_city: invoiceState.billing_city,
    shipping_state: invoiceState.billing_state,
    shipping_country_id: invoiceState.billing_country_id,
    shipping_zip_code: invoiceState.billing_zip_code,
  });

  const formatOrdersForValidation = (orders: any) => {
    const orderFields = {};

    orders.forEach((order: any, index: number) => {
      orderFields[`deductible_${index}`] = order.deductible;
      orderFields[`discount_${index}`] = order.discount;
      orderFields[`note_${index}`] = order.note;
      orderFields[`paid_${index}`] = order.paid;
      orderFields[`product_id_${index}`] = order.product_id;
      orderFields[`quantity_${index}`] = order.quantity;
      orderFields[`taxes_${index}`] = order.taxes;
    });

    return orderFields;
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setCurrentIndex(newPage - 1);
  };

  const handleClose = () => {
    dispatch(closeModal({ modal: MODALS.editInvoice }));
  };

  const canCancelInvoice = () => {
    if (!!invoice.state && invoice.state === INVOICE_STATE.final) {
      if (
        invoice.issued_date !== invoiceState.issued_date
        || invoice.contact_first_name !== invoiceState.contact_first_name
        || invoice.contact_last_name !== invoiceState.contact_last_name
        || invoice.contact_company_name !== invoiceState.contact_company_name
      ) {
        if (
          invoice.payment_status === INVOICE_PAYMENT_STATUS.scheduled
          || invoice.payment_status === INVOICE_PAYMENT_STATUS.partially_paid
        ) {
          if (!!invoice.scheduled && invoice.scheduled > 0) {
            dispatch(setAlert({
              type: ALERT_TYPE.warning,
              code: t(lang, 'messages.invoice_with_scheduled_transactions'),
            }));
            return false;
          }
          if (!invoice.scheduled || invoice.scheduled === 0) {
            dispatch(setAlert({
              type: ALERT_TYPE.warning,
              code: t(lang, 'messages.abandonned_invoice'),
            }));
            return false;
          }
        }
      }
    }
    return true;
  };

  const canUpdateInvoice = () => {
    if (!!invoice.state && invoice.state === INVOICE_STATE.final) {
      if (
        invoice.issued_date !== invoiceState.issued_date
        || invoice.contact_first_name !== invoiceState.contact_first_name
        || invoice.contact_last_name !== invoiceState.contact_last_name
        || invoice.contact_company_name !== invoiceState.contact_company_name
      ) {
        dispatch(openDialog({
          dialog: DIALOGS.replaceInvoice,
          payload: {
            contact,
          },
        }));
        return false;
      }
    }

    return true;
  };

  const getTaxesForOrder = (order: ProductOrderModel):string => JSON.stringify(order.taxes);

  const denormalizeOrders = ():OrderList => ({
    ...orderState.map((order: ProductOrderModel) => ({
      ...order,
      deductible: (order.deductible ?? 0) / 100,
      taxes: getTaxesForOrder(order),
    } as OrderModel)),
  });

  const getTaxItem = (id: number) => taxes[id];

  const getName = (state: InvoiceModel) => {
    if (!!state.contact_company_name) {
      return state.contact_company_name;
    }
    return `${state.contact_first_name} ${state.contact_last_name}`;
  };

  const getInvoiceTaxes = (orderList: ProductOrderList) => {
    const invoicesTaxes: OrderTaxModel = {};
    orderList.forEach((product:ProductOrderModel) => {
      if (product.taxes) {
        Object.keys(product.taxes).forEach((taxId) => {
          if (!taxes[taxId]) return;
          if (!invoicesTaxes[taxId]) invoicesTaxes[taxId] = 0;
          const paid = Number(product.paid ?? 0);
          const discount = Number(product.discount ?? 0);
          const quantity = Number(product.quantity ?? 0);
          invoicesTaxes[taxId] += (quantity * (paid - discount)) * Number(taxes[taxId].rate);
        });
      }
    });
    return invoicesTaxes;
  };

  const calculateTaxes = () => {
    const taxesObject = getInvoiceTaxes(orderState);
    let totalTaxes = 0;
    Object.keys(taxesObject).forEach((taxId) => {
      totalTaxes += Number(taxesObject[taxId]);
    });
    return totalTaxes;
  };

  const calculateSubtotal = () => {
    let subtotal = 0;
    orderState.forEach((order) => {
      subtotal += ((order.quantity ?? 0) * ((order.paid ?? 0) - (order.discount ?? 0)));
    });
    return subtotal;
  };

  const calculateTotal = () => (calculateSubtotal() + calculateTaxes());

  const updateTotals = () => {
    setInvoiceState({
      ...invoiceState,
      total: calculateTotal(),
      subtotal: calculateSubtotal(),
    });
  };

  const onAddProduct = () => {
    setOrderState([
      ...orderState,
      {
        product_id: 0,
        paid: 0,
        quantity: 1,
      },
    ]);
  };

  const onDeleteProduct = (orderIndex: number) => {
    const orderList = [...orderState];
    orderList.splice(orderIndex, 1);
    setOrderState(orderList);
  };

  const updateInvoiceTaxes = (orderList: ProductOrderList) => {
    const invoicesTaxes: OrderTaxModel = {};
    orderList.forEach((product:ProductOrderModel) => {
      if (product.taxes) {
        Object.keys(product.taxes).forEach((taxId) => {
          if (!taxes[taxId]) return;
          const paid = Number(product.paid ?? 0);
          const discount = Number(product.discount ?? 0);
          const quantity = Number(product.quantity ?? 0);
          invoicesTaxes[taxId] += (quantity * paid - discount) * taxes[taxId].rate;
        });
      }
    });
    setInvoiceState({
      ...invoiceState,
      taxes: invoicesTaxes,
    });
  };

  const onUpdateProduct = (value: Value, field: string, index: number) => {
    const orderList = [...orderState];
    orderList[index][field] = value;
    const newInvoiceState = {
      ...invoiceState,
      taxes: getInvoiceTaxes(orderList),
    };
    setInvoiceState(newInvoiceState);
    setOrderState(orderList);
    updateTotals();
  };

  const onProductChange = (product?: ProductModel, index?: number) => {
    const orderList = [...orderState] as ProductOrderList;

    if (index !== undefined) {
      return;
    }
    const currentOrder = { ...orderState[index ?? 0] } as ProductOrderModel;
    if (product === undefined) {
      const newEmptyOrder = {
        ...currentOrder,
        [INVOICE_ORDER_FIELDS.product_id]: 0,
        [INVOICE_ORDER_FIELDS.paid]: 0,
        [INVOICE_ORDER_FIELDS.discount]: 0,
        [INVOICE_ORDER_FIELDS.deductible]: 0,
      } as ProductOrderModel;
      orderList.splice(index ?? 0, 1, newEmptyOrder);
      setOrderState(orderList);
      updateInvoiceTaxes(orderList);
      updateTotals();
      return;
    }

    const newOrder = {
      ...currentOrder,
      [INVOICE_ORDER_FIELDS.paid]: product.price || 0,
      [INVOICE_ORDER_FIELDS.discount]: product.discount || 0,
      [INVOICE_ORDER_FIELDS.deductible]: (product?.deductible || 0) * 100,
    } as ProductOrderModel;
    orderList.splice(index ?? 0, 1, newOrder);
    setOrderState(orderList);
    setInvoiceState({
      ...invoiceState,
      title: product[`name_${lang}`],
      program_id: product.program_id,
      account_id: product.account_id,
      department_id: product.department_id,
      bank_account_id: product.bank_account_id,
      payment_gateway_cc_id: product.payment_gateway_cc_id,
      payment_gateway_eft_id: product.payment_gateway_eft_id,
      taxes: getInvoiceTaxes(orderList),
    });
  };

  const onFieldChange = (value: Value, field: string) => {
    setInvoiceState({
      ...invoiceState,
      [field]: value,
    });
  };

  const onFundraisingFieldChange = useCallback((value: Value, field: string) => {
    setFundraisingState((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const onInstallmentChange = (installmentNumber: string) => {
    onFieldChange(parseInt(installmentNumber, 10), 'installments');
  };

  const onContactChange = (id: number, field: Fields) => {
    onFieldChange(id, field);
    setContactId(id);
    dispatch(getContactAction(id, FORM.edit_invoice));
  };

  const getTaxPercentage = (rate:number) => `${Math.ceil(rate * 100000) / 1000}%`;

  const renderTaxes = () => {
    const taxesObject = getInvoiceTaxes(orderState);
    return (
      <>
        { Object.keys(taxesObject).map((taxId: string, index) => {
          const tax = getTaxItem(Number(taxId));
          const abbreviation = tax[`abbreviation_${lang}`];
          return (
            <div
              key={`tax-item-${index}`}
              style={{ textAlign: 'right' }}>
              <span id={`tax-item-${abbreviation}`}>
                {tax[`name_${lang}`]} ({getTaxPercentage(tax.rate)}):&nbsp;
              </span>
              <Amount>{formatter.format(taxesObject[taxId])}</Amount>
            </div>
          );
        }) }
      </>
    );
  };

  const renderInvoiceDetails = () => {
    if (isEmpty(invoiceState)) {
      return '';
    }
    return (
      <Row height="auto">
        <Col2>
          <b>{ t(lang, 'forms.invoices.billing_address') }:</b>
          <div>{ invoiceState.billing_address }</div>
          { !!invoiceState.billing_suite && (
            <div>{invoiceState.billing_suite}</div>
          )}
          <div>
            { invoiceState.billing_city },&nbsp;
            { invoiceState.billing_state }
          </div>
          <div>{ invoiceState.billing_zip_code }</div>
          <div>
            { `${!!countries[invoiceState.billing_country_id ?? 0] && countries[invoiceState.billing_country_id ?? 0].name_en}` }
          </div>
        </Col2>
        <Col2>
          <b>{ t(lang, 'forms.invoices.shipping_address') }:</b>
          <div>
            { invoiceState.shipping_address } ({ invoiceState.shipping_suite })
          </div>
          <div>
            { invoiceState.shipping_city },&nbsp;
            { invoiceState.shipping_state }
          </div>
          <div>{ invoiceState.shipping_zip_code }</div>
          <div>{ `${!!countries[invoiceState.shipping_country_id ?? 0] && countries[invoiceState.shipping_country_id ?? 0].name_en}` }</div>
        </Col2>
      </Row>
    );
  };

  const renderContactFullName = () => {
    if (isEmpty(invoice)) return <></>;
    return (
      <Row>
        <b>
          {getName(invoiceState)}&nbsp;
          <EditIcon
            title={t(lang, 'menus.edit_invoice_details')}
            onClick={() => setInvoiceDetailsViewState(!invoiceDetailsViewState)} />
        </b>
      </Row>
    );
  };

  const isDue = () => {
    switch (invoiceState.payment_status) {
      case INVOICE_PAYMENT_STATUS.paid:
      case INVOICE_PAYMENT_STATUS.scheduled:
        return false;
      default:
        if (!moment.isMoment(invoice.due_date)) return false;
        return moment(invoiceState.due_date).isBefore(moment());
    }
  };

  const hasWarning = () => {
    switch (invoice.payment_status) {
      case INVOICE_PAYMENT_STATUS.scheduled_due:
        return true;
      default:
        return false;
    }
  };

  const getStatusColor = () => {
    const color = INVOICE_PAYMENT_STATUS_COLOR[
      invoice.payment_status ?? INVOICE_PAYMENT_STATUS_COLOR.not_paid
    ];
    return hasWarning() ? COLORS.error : color;
  };

  const disableCurrentField = (field: string) => {
    if (processing()) return true;
    return !allowedFields.includes(field);
  };

  const isPaymentDisabled = () => (
    processing()
    || (invoiceState.payment_status === INVOICE_PAYMENT_STATUS.paid)
    || (invoiceState.state === INVOICE_STATE.draft)
    || disableCurrentField(InvoiceOperations.make_payment)
  );

  const getIsLinked = () => {
    if (fundraisingState) {
      return fundraisingState.is_linked;
    }
    return false;
  };

  const handleSubmit = (
    event: MouseEvent<HTMLElement>,
    state: INVOICE_STATE,
    needConfirmation?: Boolean,
  ) => {
    event.preventDefault();
    dispatch(setError({ [FORM.edit_invoice]: {} }));
    if (!canUpdateInvoice() && (needConfirmation !== false)) {
      return false;
    }
    try {
      InvoiceValidator(lang).validateSync({ ...invoiceState }, { abortEarly: false });
      InvoiceOrderValidator(orderState, lang).validateSync(
        formatOrdersForValidation(orderState),
        { abortEarly: false },
      );
      if (fundraisingState.is_linked) {
        FundraisingLinkValidator()
          .validateSync(fundraisingState, { abortEarly: false });
      }
    } catch (validationErrors: any) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: t(lang, 'forms.contact.validation_errors'),
      }));
      const errorBag = {} as ErrorBag;
      validationErrors.inner.forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
      dispatch(setError({ [FORM.edit_invoice]: errorBag }));
      return false;
    }

    let currentInvoice = invoiceState;
    if (invoiceState.sameAsBilling) {
      currentInvoice = setShippingFromBilling();
    }

    const payload = {
      invoice: {
        ...currentInvoice,
        issued_date: fromOrgTimezone(currentInvoice.issued_date, timezone),
        due_date: fromOrgTimezone(currentInvoice.due_date, timezone),
        payment_gateway_cc_id: currentInvoice.payment_gateway_cc_id || null,
        payment_gateway_eft_id: currentInvoice.payment_gateway_eft_id || null,
        state,
      },
      order: denormalizeOrders(),
      event: fundraisingState.is_linked ? fundraisingState : undefined,
    };
    if (!payload.invoice.hasInstallments) {
      payload.invoice.installments = 0;
    }
    delete payload.invoice.hasInstallments;
    dispatch(putInvoiceAction(selections[currentIndex] as number, payload));
    return true;
  };

  return (
    <>
      <Header height="86px">
        <Row>
          <Title>
            { t(lang, 'forms.invoices.edit_invoices') }&nbsp;
            #{ invoiceState.invoice_number }
          </Title>
          <Button
            style={{ marginLeft: 'auto' }}
            variant="contained"
            color="primary"
            size="small"
            onClick={handleClose}
            disabled={processing()}>
            { t(lang, 'misc.close') }
          </Button>
        </Row>
        <Row>
          <CancelInvoiceButton
            disabled={processing() || !canCancelInvoice()}
            invoiceId={selections[currentIndex]} />
          { (invoiceState.state === INVOICE_STATE.draft) && (
          <Tooltip
            title={t(lang, 'menus.save_as_draft')}
            placement="top-start">
            <span>
              <ActionButton
                disabled={processing()}
                category={MENU_BUTTON_CATEGORY.action}
                onClick={(e) => handleSubmit(e, INVOICE_STATE.draft)}>
                <SaveAltIcon />
              </ActionButton>
            </span>
          </Tooltip>
          )}
          <Tooltip
            title={t(lang, 'menus.save_as_final')}
            placement="top-start">
            <span>
              <ActionButton
                disabled={processing() || disableCurrentField(InvoiceOperations.make_final)}
                category={MENU_BUTTON_CATEGORY.action}
                onClick={(e) => handleSubmit(e, INVOICE_STATE.final)}>
                <SaveIcon />
              </ActionButton>
            </span>
          </Tooltip>
          <DuplicateInvoiceButton
            invoice={invoiceState}
            disabled={disableCurrentField(InvoiceOperations.duplicate_invoice)} />
          <MakePaymentButton
            disabled={isPaymentDisabled()}
            invoiceId={invoiceState.id} />
          <ManageAccountsButton disabled={disableCurrentField(InvoiceOperations.manage_accounts)} />
          <ManageDepartmentsButton
            disabled={disableCurrentField(InvoiceOperations.manage_departments)} />
          <ManageProgramsButton
            disabled={disableCurrentField(InvoiceOperations.manage_programs)} />
          <ManageProductsButton
            disabled={disableCurrentField(InvoiceOperations.manage_products)} />
          <ShowPaymentsForInvoiceButton
            invoiceId={selections[currentIndex]}
            disabled={disableCurrentField(InvoiceOperations.show_invoice_payments)} />
          <AddInvoiceContactButton />
          <DownloadSelectedInvoiceButton
            invoiceId={selections[currentIndex]}
            disabled={disableCurrentField(InvoiceOperations.download_selected)} />
          <SendInvoiceByEmailButton
            invoiceId={selections[currentIndex]}
            disabled={disableCurrentField(InvoiceOperations.send_by_email)} />
        </Row>
      </Header>
      <Row style={{ marginTop: '4px', backgroundColor: 'white' }}>
        <span><b>{ t(lang, 'forms.invoices.paid') }:</b> { formatter.format(invoiceState.paid ?? 0) }</span>
        <span><b>{ t(lang, 'forms.invoices.scheduled') }:</b> { formatter.format(invoiceState.scheduled ?? 0) }</span>
        <span><b>Balance:</b> { formatter.format(invoiceState.balance ?? 0) }</span>
        <span>
          <b>{ t(lang, 'forms.invoices.payment_status') }:</b>&nbsp;
          { (hasWarning() || isDue()) && (
            <Warning />
          ) }
          <span
            style={{
              color: getStatusColor(),
            }}>
            { t(lang, `forms.invoices.${invoice.payment_status}`) }
          </span>
        </span>
        <span>
          <b>{ t(lang, 'forms.invoices.state') }:</b>&nbsp;
          <span>
            { t(lang, `forms.invoices.${invoice.state}`) }
          </span>
        </span>
      </Row>
      <PaginationUI
        count={selections.length}
        page={page}
        onChange={handlePageChange} />
      <Scroller>
        <Form maxWidth={MODALS_SIZE.large}>
          <Subtitle noMargin style={{ marginBottom: '8px' }}>
            {t(lang, 'forms.invoices.invoice_informations')}
          </Subtitle>
          <Row height="64px">
            <FormTextField
              form={FORM.add_invoice}
              label={t(lang, 'forms.invoices.title')}
              name={INVOICE_FIELDS.title}
              onChange={onFieldChange}
              required
              value={invoiceState.title || ''}
              disabled={disableCurrentField(INVOICE_FIELDS.title)} />
            <Col2>
              <FormDatePicker
                form={FORM.edit_invoice}
                required
                disabled={disableCurrentField(INVOICE_FIELDS.due_date)}
                error={isDue()}
                label={t(lang, 'forms.invoices.due_date')}
                name={INVOICE_FIELDS.due_date}
                onChange={
                    (value: MaterialUiPickersDate) => onFieldChange(
                      value,
                      INVOICE_FIELDS.due_date,
                    )
                  }
                value={invoiceState.due_date || moment()} />
            </Col2>
            <Col2>
              <FormDatePicker
                form={FORM.edit_invoice}
                required
                disabled={disableCurrentField(INVOICE_FIELDS.issued_date)}
                label={t(lang, 'forms.invoices.issued_date')}
                name={INVOICE_FIELDS.issued_date}
                onChange={
                    (value: MaterialUiPickersDate) => onFieldChange(
                      value,
                      INVOICE_FIELDS.issued_date,
                    )
                  }
                value={invoiceState.issued_date || moment()} />
            </Col2>
          </Row>
          <Subtitle style={{ marginBottom: '16px' }}>
            {t(lang, 'forms.invoices.contacts')}
          </Subtitle>
          <Row height="96px" alignItems="start">
            <Col2>
              <ContactSelector
                disabled={disableCurrentField(INVOICE_FIELDS.contact_id)}
                autocompleteId={AUTOCOMPLETE_ID.add_invoice_contact}
                onChange={(id: number) => onContactChange(id, INVOICE_FIELDS.contact_id)}
                label="Contact"
                form={FORM.edit_invoice}
                name={INVOICE_FIELDS.contact_id}
                contactId={invoiceState.contact_id}
                hideEdit={!invoiceState.contact_id} />
            </Col2>
            <Col2>
              <ContactSelector
                autocompleteId={AUTOCOMPLETE_ID.edit_invoice_seller}
                onChange={(id: number) => onFieldChange(id, INVOICE_FIELDS.seller_id)}
                label="Seller"
                disabled={disableCurrentField(INVOICE_FIELDS.seller_id)}
                form={FORM.edit_invoice}
                name={INVOICE_FIELDS.seller_id}
                contactId={invoiceState.seller_id}
                hideEdit={!invoiceState.seller_id} />
            </Col2>
          </Row>
          <Row height="96px" alignItems="start">
            <Col2>
            &nbsp;
            </Col2>
            <Col2>
              <ContactSelector
                autocompleteId={AUTOCOMPLETE_ID.edit_invoice_payer}
                onChange={(id: number) => onFieldChange(id, INVOICE_FIELDS.contact_payer_id)}
                label="Payer"
                disabled={disableCurrentField(INVOICE_FIELDS.contact_payer_id)}
                form={FORM.edit_invoice}
                name={INVOICE_FIELDS.contact_payer_id}
                contactId={invoiceState.contact_payer_id}
                hideEdit={!invoiceState.contact_payer_id} />
            </Col2>
          </Row>

          {renderContactFullName()}
          {(invoiceDetailsViewState) && renderInvoiceDetails()}
          {!(invoiceDetailsViewState) && (
          <div>
            <Row height="auto">
              <Col2>
                <FormTextField
                  form={FORM.edit_invoice}
                  label={t(lang, 'fields.invoices.contact_first_name')}
                  name={INVOICE_FIELDS.contact_first_name}
                  onChange={onFieldChange}
                  value={invoiceState.contact_first_name || ''} />
              </Col2>
              <Col2>
                <FormTextField
                  form={FORM.edit_invoice}
                  label={t(lang, 'forms.contact.last_name')}
                  name={INVOICE_FIELDS.contact_last_name}
                  onChange={onFieldChange}
                  value={invoiceState.contact_last_name || ''} />
              </Col2>
            </Row>
            <Row height="auto">
              <Col2>
                <FormTextField
                  form={FORM.edit_invoice}
                  label={t(lang, 'forms.contact.company_name')}
                  name={INVOICE_FIELDS.contact_company_name}
                  onChange={onFieldChange}
                  value={invoiceState.contact_company_name || ''} />
              </Col2>
            </Row>
            <Row height="auto">
              <Col2>
                <Subtitle noMargin style={{ marginBottom: '8px' }}>
                  {t(lang, 'forms.contact.header_billing_address')}
                </Subtitle>
                <hr />
                <AddressSearch
                  onChange={onFieldChange}
                  onAddressChange={
                          (addresse: AddressModel) => {
                            setAddressFromGoogle(addresse, AddressType.billing);
                          }
                        }
                  value={{ description: invoiceState.billing_address || '' }}
                  name="billing_address"
                  form={FORM.edit_invoice} />
                <FormTextField
                  form={FORM.edit_invoice}
                  label={t(lang, 'forms.contact.suite')}
                  name={INVOICE_FIELDS.billing_suite}
                  onChange={onFieldChange}
                  value={invoiceState.billing_suite || ''} />
                <FormTextField
                  form={FORM.edit_invoice}
                  label={t(lang, 'forms.contact.city')}
                  name={INVOICE_FIELDS.billing_city}
                  onChange={onFieldChange}
                  value={invoiceState.billing_city || ''} />
                <FormTextField
                  form={FORM.edit_invoice}
                  label={t(lang, 'forms.contact.state')}
                  name={INVOICE_FIELDS.billing_state}
                  onChange={onFieldChange}
                  value={invoiceState.billing_state || ''} />
                <CountriesSelector
                  form={FORM.edit_invoice}
                  onChange={(id) => onFieldChange(id, 'billing_country_id')}
                  value={invoiceState.billing_country_id || undefined}
                  name="billing_country_id" />
                <FormTextField
                  form={FORM.edit_invoice}
                  label={t(lang, 'forms.contact.zip_code')}
                  name={INVOICE_FIELDS.billing_zip_code}
                  onChange={onFieldChange}
                  value={invoiceState.billing_zip_code || ''} />
              </Col2>
              <Col2>
                <Subtitle noMargin style={{ marginBottom: '8px' }}>
                  {t(lang, 'forms.contact.header_shipping_address')}
                </Subtitle>
                <hr />
                <AddressSearch
                  onChange={onFieldChange}
                  onAddressChange={
                            (addresse: AddressModel) => {
                              setAddressFromGoogle(addresse, AddressType.shipping);
                            }
                          }
                  value={{ description: invoiceState.shipping_address || '' }}
                  name="shipping_address"
                  form={FORM.edit_invoice} />
                <FormTextField
                  form={FORM.edit_invoice}
                  label={t(lang, 'forms.contact.suite')}
                  name="shipping_suite"
                  onChange={onFieldChange}
                  value={invoiceState.shipping_suite || ''} />
                <FormTextField
                  form={FORM.edit_invoice}
                  label={t(lang, 'forms.contact.city')}
                  name="shipping_city"
                  onChange={onFieldChange}
                  value={invoiceState.shipping_city || ''} />
                <FormTextField
                  form={FORM.edit_invoice}
                  label={t(lang, 'forms.contact.state')}
                  name="shipping_state"
                  onChange={onFieldChange}
                  value={invoiceState.shipping_state || ''} />
                <CountriesSelector
                  form={FORM.edit_invoice}
                  onChange={(id) => onFieldChange(id, 'shipping_country_id')}
                  value={invoiceState.shipping_country_id || undefined}
                  name="shipping_country_id" />
                <FormTextField
                  form={FORM.edit_invoice}
                  label={t(lang, 'forms.contact.zip_code')}
                  name="shipping_zip_code"
                  onChange={onFieldChange}
                  value={invoiceState.shipping_zip_code || ''} />
              </Col2>
            </Row>
          </div>
          )}
          <Subtitle style={{ marginBottom: '16px' }}>{t(lang, 'forms.invoices.orders')}</Subtitle>

          <OrdersDisplayer
            form={FORM.edit_invoice}
            onUpdateProduct={onUpdateProduct}
            orders={orderState}
            onDeleteProduct={onDeleteProduct}
            onAddProduct={onAddProduct}
            onProductChange={onProductChange}
            invoiceState={invoice.state ?? INVOICE_STATE.draft}
            disabled={disableCurrentField(InvoiceOperations.manage_orders)} />
          <Row>
            <span style={{ marginRight: '0px', marginLeft: 'auto' }}>
              {t(lang, 'forms.invoices.subtotal')}:&nbsp;
              <Amount>{formatter.format(calculateSubtotal())}</Amount>
            </span>
          </Row>
          <Row>
            <span style={{ marginRight: '0px', marginLeft: 'auto' }}>
              <i>{renderTaxes()}</i>
            </span>
          </Row>
          <Row>
            <span style={{ marginRight: '0px', marginLeft: 'auto' }}>
              <b>
                Total:&nbsp;
                <Amount>{formatter.format(calculateTotal())}</Amount>
              </b>
            </span>
          </Row>
          <Subtitle style={{ marginBottom: '8px' }}>Payment</Subtitle>
          <Row>
            <FormCheckbox
              name={INVOICE_FIELDS.issue_tax_receipt}
              disabled={disableCurrentField(INVOICE_FIELDS.issue_tax_receipt)}
              label={t(lang, 'forms.invoices.issue_tax_receipt')}
              checked={Boolean(invoiceState.issue_tax_receipt)}
              onChange={onFieldChange} />
          </Row>
          <Row style={{ marginTop: '16px' }}>
            <FormCheckbox
              name="hasInstallments"
              disabled={disableCurrentField(INVOICE_FIELDS.allow_installment)}
              label="Allow installments"
              checked={!!invoiceState.hasInstallments}
              onChange={onFieldChange} />
          </Row>
          {invoiceState.hasInstallments && (
            <Row>
              <Typography
                variant="body1"
                style={{ color: 'rgb(0, 0, 0, .75)' }}>
                Number of installment(s):
              </Typography>
              <TextField
                style={{ width: '35px' }}
                name="installments"
                type="number"
                onChange={(e) => onInstallmentChange(e.target.value as string)}
                value={invoiceState.installments} />
            </Row>
          )}
          <Row height="64px" style={{ marginTop: '16px' }}>
            <Col2 style={{ marginTop: '-22px' }}>
              <GatewaySelector
                disabled={processing()}
                form={FORM.add_invoice}
                name="payment_gateway_eft_id"
                onChange={(value) => onFieldChange(value, 'payment_gateway_eft_id')}
                value={invoiceState.payment_gateway_eft_id || 0}
                type={ProcessingType.eft}
                noMarginTop />
            </Col2>
            <Col2 style={{ marginTop: '-22px' }}>
              <GatewaySelector
                disabled={processing()}
                form={FORM.add_invoice}
                name="payment_gateway_cc_id"
                onChange={(value) => onFieldChange(value, 'payment_gateway_cc_id')}
                value={invoiceState.payment_gateway_cc_id || 0}
                type={ProcessingType.cc}
                noMarginTop />
            </Col2>
          </Row>
          <Row style={{ marginTop: '-16px' }}>
            <Col2>
              <OrganisationBankAccountSelector
                value={invoiceState.bank_account_id || undefined}
                onChange={(id: number) => onFieldChange(id, INVOICE_FIELDS.bank_account_id)}
                form={FORM.add_invoice}
                name={INVOICE_FIELDS.bank_account_id}
                disabled={disableCurrentField(INVOICE_FIELDS.bank_account_id)} />
            </Col2>
          </Row>
          <Subtitle style={{ marginBottom: '8px' }}>Fundraising</Subtitle>
          <Row height="auto">
            <FormCheckbox
              label="Link this invoice to a fundraising"
              name="is_linked"
              onChange={onFundraisingFieldChange}
              checked={getIsLinked()} />
          </Row>
          { getIsLinked() && (
          <>
            <Row height="auto">
              <FundraisingSelector
                form={FORM.edit_invoice}
                label="Fundraising"
                name="event_id"
                value={fundraisingState.event_id}
                onChange={(id) => onFundraisingFieldChange(id, 'event_id')} />
              <AmbassadorSelector
                form={FORM.edit_invoice}
                fundraisingId={fundraisingState.event_id}
                label="Ambassador"
                value={fundraisingState.ambassador_id}
                name="ambassador_id"
                onChange={(id) => onFundraisingFieldChange(id, 'ambassador_id')} />
            </Row>
            <Row>
              <Col4>
                <FormTextField
                  form={FORM.edit_invoice}
                  label="Amount multiplier"
                  name="amount_multiplier"
                  onChange={onFundraisingFieldChange}
                  value={fundraisingState.amount_multiplier} />
              </Col4>
            </Row>
            <Row height="auto">
              <Col2>
                <FormTextField
                  form={FORM.edit_invoice}
                  label="Donor name displayed"
                  name="donor_name_displayed"
                  onChange={onFundraisingFieldChange}
                  value={fundraisingState.donor_name_displayed} />
              </Col2>
              <Col2>
                <FormCheckbox
                  label="Anonymous Donor"
                  name="is_anonymous"
                  onChange={onFundraisingFieldChange}
                  checked={!!fundraisingState.is_anonymous}
                  disabled={processing()} />
              </Col2>
            </Row>
            <Row height="auto">
              <FormTextField
                form={FORM.edit_invoice}
                label="Donor Comment"
                name="donor_comment"
                onChange={onFundraisingFieldChange}
                multiline
                minRows={3}
                maxRows={5}
                value={fundraisingState.donor_comment} />
            </Row>
            <Row height="auto">
              <Col2>
                <FormTextField
                  form={FORM.edit_invoice}
                  label="Source reference"
                  name="source_reference"
                  onChange={onFundraisingFieldChange}
                  value={fundraisingState.source_reference} />
              </Col2>
              <Col2>
                <FormTextField
                  form={FORM.edit_invoice}
                  label="Source reference URL"
                  name="source_reference_url"
                  onChange={onFundraisingFieldChange}
                  value={fundraisingState.source_reference_url} />
              </Col2>
            </Row>
          </>
          )}
          <Subtitle style={{ marginBottom: '8px' }}>Meta Data</Subtitle>
          <Row height="auto">
            <Col2>
              <MetadataSelector
                type={MetadataType.program}
                onChange={(id) => onFieldChange(id, INVOICE_FIELDS.program_id)}
                form={FORM.edit_invoice}
                name={INVOICE_FIELDS.program_id}
                value={invoiceState.program_id || undefined}
                disabled={disableCurrentField(INVOICE_FIELDS.program_id)}
                noMarginTop />
            </Col2>
            <Col2>
              <MetadataSelector
                type={MetadataType.department}
                onChange={(id) => onFieldChange(id, INVOICE_FIELDS.department_id)}
                form={FORM.edit_invoice}
                name={INVOICE_FIELDS.department_id}
                value={invoiceState.department_id || undefined}
                disabled={disableCurrentField(INVOICE_FIELDS.department_id)}
                noMarginTop />
            </Col2>
          </Row>
          <Row height="auto" style={{ marginTop: '-24px' }}>
            <Col2>
              <MetadataSelector
                type={MetadataType.account}
                onChange={(id) => onFieldChange(id, INVOICE_FIELDS.account_id)}
                form={FORM.add_invoice}
                name={INVOICE_FIELDS.account_id}
                value={invoiceState.account_id || undefined}
                disabled={disableCurrentField(INVOICE_FIELDS.account_id)}
                noMarginTop />
            </Col2>
          </Row>
          <Row height="auto" style={{ marginTop: '-24px' }}>
            <FormTextField
              form={FORM.edit_invoice}
              label={t(lang, 'forms.invoices.note')}
              multiline
              minRows="4"
              name={INVOICE_FIELDS.note}
              onChange={onFieldChange}
              value={invoiceState.note || ''}
              disabled={disableCurrentField(INVOICE_FIELDS.note)} />
          </Row>
        </Form>
      </Scroller>
      <ReplaceInvoiceDialog
        handleSubmit={(e) => handleSubmit(e, INVOICE_STATE.final, false)} />
    </>
  );
};

export default EditInvoiceForm;
